import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStores } from "../../stores/setup/use-store";
import { ProgressBar } from "./components/ProgressBar";
import { StepController } from "./components/StepController";
import { useAlertCreationDrawer } from "./hooks/useAlertCreationDrawer";
import { useAlertSteps } from "./hooks/useAlertSteps";
import { useFormValuesSync } from "./hooks/useFormValuesSync";
import { CreateAlertFormValues } from "./types";
import { handleSubmitWithScroll } from "./util/handleSubmitWithScroll";
import { ChannelMismatchModal } from "../OceanWeatherIntelligence/InsightAlert/Components/ChannelMismatchModal";
import { useChannelValidation } from "../OceanWeatherIntelligence/InsightAlert/hooks/useChannelValidation";

interface AlertCreateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  showPhoneVerificationModal: boolean;
  onPhoneModalOpen: () => void;
}

export const AlertCreateDrawer: React.FC<AlertCreateDrawerProps> = observer(
  ({ isOpen, onClose, onPhoneModalOpen, showPhoneVerificationModal }) => {
    const {
      t,
      initialValues,
      validationSchema,
      resetInitialValues,
      handleDrawingMode,
      handlePointSelectionMode,
      handleGeoJsonLoaded,
      STEPS,
      selectedGeofence,
      environmentalVariables,
      selectedTemplate,
      handleOnSaveFormAlertModal,
      clearGeofence,
      clearSelectedStation,
      isSubmitting,
      setIsGeofenceLoadedByGeoJson,
      resetTemplateSelection,
      areas,
      handleAreaSelection,
      handleEnableAreaSelector,
      areaSelectorVisible,
      usersTableSelected,
      setUsersTableSelected,
      alertUsers,
    } = useAlertCreationDrawer({
      isOpen,
      onAlertCreatedSuccess: onPhoneModalOpen,
      showPhoneVerificationModal,
    });

    const {
      dataStores: { insightAlertStore, forecastChartPageStore },
    } = useStores();

    const { checkChannelMismatch } = useChannelValidation();

    const {
      isOpen: isChannelMismatchModalOpen,
      onOpen: onChannelMismatchModalOpen,
      onClose: onChannelMismatchModalClose,
    } = useDisclosure();

    const [mismatchedChannels, setMismatchedChannels] = useState<string[]>([]);
    const [pendingSubmitValues, setPendingSubmitValues] =
      useState<CreateAlertFormValues | null>(null);

    const { currentStep, nextStep, prevStep, resetSteps, validateCurrentStep } =
      useAlertSteps(STEPS.length);

    // Ref for the drawer body to scroll to top when step changes
    const drawerBodyRef = useRef<HTMLDivElement>(null);

    // Scroll to top when step changes
    useEffect(() => {
      if (drawerBodyRef.current) {
        drawerBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, [currentStep]);

    const handleClose = useCallback(() => {
      // Don't reset data when closing due to point/area selection mode
      // (user is navigating to Forecast Charts to select location)
      const isInSelectionMode =
        forecastChartPageStore.isAreaDrawingMode ||
        forecastChartPageStore.isPointSelectionMode;

      onClose();
      resetSteps();

      if (!isInSelectionMode) {
        resetInitialValues();
        resetTemplateSelection();
      }
    }, [
      onClose,
      resetSteps,
      resetInitialValues,
      resetTemplateSelection,
      forecastChartPageStore,
    ]);

    const handleNextStep = useCallback(
      async (
        validateForm: () => Promise<any>,
        setTouched: (touched: any, shouldValidate?: boolean) => void,
        setFieldError: (field: string, message: string | undefined) => void,
        values: any,
      ) => {
        if (
          insightAlertStore.isGeofenceInvalid ||
          insightAlertStore.isPointInvalid
        ) {
          return;
        }

        const isValid = await validateCurrentStep(
          currentStep,
          validateForm,
          setTouched,
          setFieldError,
          values,
        );

        if (isValid) {
          nextStep();
        }
      },
      [
        insightAlertStore.isGeofenceInvalid,
        insightAlertStore.isPointInvalid,
        validateCurrentStep,
        currentStep,
        nextStep,
      ],
    );

    const CurrentStepComponent = useMemo(
      () => STEPS[currentStep].component as React.ComponentType<any>,
      [STEPS, currentStep],
    );

    // Handler for category change - resets steps to avoid invalid step index
    const handleCategoryChange = useCallback(() => {
      resetSteps();
    }, [resetSteps]);

    const handleFormSubmit = useCallback(
      (values: CreateAlertFormValues) => {
        const mismatched = checkChannelMismatch(
          insightAlertStore.operationRuleAlertUserSelected,
          values.channels || [],
        );

        if (mismatched.length > 0) {
          setMismatchedChannels(mismatched);
          setPendingSubmitValues(values);
          onChannelMismatchModalOpen();
        } else {
          handleOnSaveFormAlertModal(values, handleClose);
        }
      },
      [
        handleOnSaveFormAlertModal,
        handleClose,
        checkChannelMismatch,
        insightAlertStore.operationRuleAlertUserSelected,
        onChannelMismatchModalOpen,
      ],
    );

    const handleChannelMismatchConfirm = useCallback(() => {
      if (pendingSubmitValues) {
        handleOnSaveFormAlertModal(pendingSubmitValues, handleClose);
        setPendingSubmitValues(null);
      }
      onChannelMismatchModalClose();
    }, [
      pendingSubmitValues,
      handleOnSaveFormAlertModal,
      handleClose,
      onChannelMismatchModalClose,
    ]);

    const handleChannelMismatchCancel = useCallback(() => {
      setPendingSubmitValues(null);
      onChannelMismatchModalClose();
    }, [onChannelMismatchModalClose]);

    const syncFormValues = useCallback(
      (values: CreateAlertFormValues) => {
        insightAlertStore.setCreateFormValues(values);
      },
      [insightAlertStore],
    );

    return (
      <>
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={handleClose}
          size="xl"
        >
          <Formik<CreateAlertFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
            validateOnBlur={true}
            validateOnChange={false}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              submitForm,
              setFieldValue,
              setFieldError,
              validateForm,
              setTouched,
            }) => {
              useFormValuesSync({
                values,
                onSync: syncFormValues,
                isOpen,
                debounceMs: 300,
              });

              return (
                <>
                  <DrawerOverlay />
                  <DrawerContent bg="white">
                    <DrawerCloseButton color={"black"} />
                    <DrawerHeader>
                      <Heading
                        display={"flex"}
                        size="md"
                        color="black"
                        gap="16px"
                      >
                        <Text mt={"3px"}>{t("createAlert")}</Text>
                      </Heading>

                      <ProgressBar currentStep={currentStep} steps={STEPS} />
                    </DrawerHeader>

                    <DrawerBody ref={drawerBodyRef} color="black">
                      <Form onSubmit={handleSubmit}>
                        <Box>
                          <CurrentStepComponent
                            values={values}
                            errors={errors}
                            touched={touched}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            selectedGeofence={selectedGeofence}
                            selectedTemplate={selectedTemplate}
                            handleDrawingMode={handleDrawingMode}
                            handlePointSelectionMode={handlePointSelectionMode}
                            handleGeoJsonLoaded={handleGeoJsonLoaded}
                            environmentalVariables={environmentalVariables}
                            t={t}
                            showGeofenceError={
                              insightAlertStore.isGeofenceInvalid &&
                              insightAlertStore.isGeofenceLoadedByGeoJson
                            }
                            clearGeofence={clearGeofence}
                            clearSelectedStation={clearSelectedStation}
                            setIsGeofenceLoadedByGeoJson={
                              setIsGeofenceLoadedByGeoJson
                            }
                            areas={areas}
                            handleAreaSelection={handleAreaSelection}
                            handleEnableAreaSelector={handleEnableAreaSelector}
                            areaSelectorVisible={areaSelectorVisible}
                            usersTableSelected={usersTableSelected}
                            setUsersTableSelected={setUsersTableSelected}
                            alertUsers={alertUsers}
                            onCategoryChange={handleCategoryChange}
                            showAlertTypeSelector={false}
                          />
                        </Box>
                      </Form>
                    </DrawerBody>

                    <Box px={6} py={4} borderTopWidth="1px">
                      <StepController
                        currentStep={currentStep}
                        totalSteps={STEPS.length}
                        onNext={() =>
                          handleNextStep(
                            validateForm,
                            setTouched,
                            setFieldError,
                            values,
                          )
                        }
                        onBack={prevStep}
                        onCancel={handleClose}
                        onSubmit={() =>
                          handleSubmitWithScroll({
                            initialValues,
                            validateForm,
                            setTouched,
                            submitForm,
                          })
                        }
                        isNextDisabled={
                          insightAlertStore.isGeofenceInvalid ||
                          insightAlertStore.isPointInvalid
                        }
                        t={t}
                        isSubmitting={isSubmitting}
                      />
                    </Box>
                  </DrawerContent>
                </>
              );
            }}
          </Formik>
        </Drawer>

        <ChannelMismatchModal
          isOpen={isChannelMismatchModalOpen}
          onClose={handleChannelMismatchCancel}
          onConfirm={handleChannelMismatchConfirm}
          t={t}
          mismatchedChannels={mismatchedChannels}
        />
      </>
    );
  },
);

export default AlertCreateDrawer;
